// WAW: the home-page hero with moving photos (sections/waw-hero-slides.liquid). Moves the is-active class from one
// slide to the next every data-interval seconds; the fade and the zoom are CSS (section-waw-hero-slides.css). No
// layout work here. Pauses while the tab is hidden, on the pause button, and never auto-advances for people who
// asked for reduced motion (the dots still work). With one slide it does nothing. No dependencies.
class WawHeroSlides extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.waw-hero-slides__slide'));
    if (this.slides.length < 2) return;

    this.dots = Array.from(this.querySelectorAll('[data-slide-to]'));
    this.pauseButton = this.querySelector('[data-pause]');
    this.pauseLabel = this.querySelector('[data-pause-label]');
    this.interval = Math.max(1, Number(this.dataset.interval) || 4) * 1000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.current = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    this.paused = false;
    this.timer = null;

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    this.onBlockSelect = (event) => this.onEditorSelect(event, true);
    this.onBlockDeselect = (event) => this.onEditorSelect(event, false);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.show(Number(dot.dataset.slideTo));
        this.restart();
      });
    });
    if (this.pauseButton) this.pauseButton.addEventListener('click', () => this.togglePause());
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.show(this.current);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  /** Shows one slide; the others are hidden from assistive tech too. */
  show(index) {
    const count = this.slides.length;
    this.current = ((index % count) + count) % count;
    this.slides.forEach((slide, i) => {
      const active = i === this.current;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.current;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  next() {
    this.show(this.current + 1);
  }

  /** Auto-advance, unless paused, hidden, or reduced motion is asked for. */
  start() {
    if (this.timer || this.paused || document.hidden || this.reduced.matches) return;
    this.timer = window.setInterval(() => this.next(), this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  /** A full interval again from now (after a dot click), so a chosen photo is not cut short. */
  restart() {
    this.stop();
    this.start();
  }

  togglePause() {
    this.setPaused(!this.paused);
  }

  setPaused(paused) {
    this.paused = paused;
    this.classList.toggle('is-paused', paused);
    if (this.pauseButton) this.pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
    if (this.pauseLabel) this.pauseLabel.textContent = paused ? 'Play' : 'Pause';
    if (paused) this.stop();
    else this.start();
  }

  /** Theme editor: clicking a photo block shows that photo and holds it while it is selected. */
  onEditorSelect(event, selected) {
    const slide = event.target && event.target.closest ? event.target.closest('.waw-hero-slides__slide') : null;
    if (!slide || !this.contains(slide)) return;
    if (selected) {
      this.show(Number(slide.dataset.index));
      this.stop();
    } else {
      this.start();
    }
  }
}

if (!customElements.get('waw-hero-slides')) customElements.define('waw-hero-slides', WawHeroSlides);
